import { IconButton, Slider } from "@mui/material"
import LockIcon from "@mui/icons-material/Lock"
import axios from "axios"
import moment from "moment"
import "moment-timezone"
import { Form } from "react-bootstrap"
import Flatpickr from "react-flatpickr"
import CloseIcon from "@mui/icons-material/Close"
import { Link as RouterLink } from "react-router-dom"
import { Link as MuiLink } from "@mui/material"
import { FetchHelper } from "./fetchHelper"
import {
    CheckCircle,
    Inventory,
    WifiOff,
    Block,
    Warning,
    CheckCircleOutline,
    Inbox,
    LocalFireDepartment,
    TouchApp,
    Gavel,
    Scale,
    Percent,
    Business,
    EventBusy,
    CalendarToday,
} from "@mui/icons-material"
import { showError, showSuccess } from "./snackbar"

let sharedFileCache = {}
let pendingRequests = {}

export const convertUTCToLocal = (utcDt, utcDtFormat) => {
    const zone_name = moment.tz.guess()
    var toDt = moment.utc(utcDt, utcDtFormat).toDate()
    const utcepoc = moment(moment(toDt).format("YYYY-MM-DD hh:mm:ss A")).unix()
    const current_time = moment.unix(utcepoc).tz(zone_name)

    const diffInMinutes = moment().diff(current_time, "minutes")

    return {
        difference: diffInMinutes,
        time: current_time.format("YYYY-MM-DD hh:mm:ss a"),
        obj: current_time,
    }
}

export const checkFalsey = (value) => {
    return [null, "", undefined, NaN].includes(value)
}

const LOCAL_DATE_TIME_UTC_FORMATS = [
    "YYYY-MM-DD HH:mm:ss.SSSSSS",
    "YYYY-MM-DD HH:mm:ss.SSS",
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DD HH:mm",
]

const LOCAL_DATE_TIME_INPUT_FORMAT = "YYYY-MM-DD HH:mm"

export const normalizeAbsoluteOrRelativeRoute = (route = "") => {
    const trimmedRoute = String(route).trim()
    if (!trimmedRoute) return ""

    const withoutOrigin = trimmedRoute.replace(/^https?:\/\/[^/]+/i, "")
    const [pathPart, queryPart] = withoutOrigin.split("?")
    const normalizedPath = pathPart.replace(/\/+$/, "") || "/"

    return queryPart ? `${normalizedPath}?${queryPart}` : normalizedPath
}

export const normalizeRoute = (route = "") => route.replace(/^\/+/, "")

export const parseRoute = (route = "") => {
    const normalized = normalizeRoute(route)
    const [path, query = ""] = normalized.split("?")

    return {
        normalized,
        path,
        queryParams: new URLSearchParams(query),
    }
}

export const getNavigableMenuRoutes = (items = []) => {
    return items.reduce((routes, item) => {
        const children = item?.items ?? item?.children ?? []

        if (Array.isArray(children) && children.length) {
            routes.push(...getNavigableMenuRoutes(children))
            return routes
        }

        if (typeof item?.link === "string" && item.link.trim()) {
            routes.push(item.link.trim())
        }

        return routes
    }, [])
}

export const flattenMenu = (items = []) => {
    return items.flatMap((item) => {
        const children = item?.items || item?.children || []
        return [item, ...flattenMenu(children)]
    })
}

/**
 *
 * Input example:
 * - "super_admin"
 *
 * Output example:
 * - "Super Admin"
 *
 * @param {string} name - Role name in snake_case format
 * @returns {string} Formatted role name for display
 */
export const formatRoleName = (name) => {
    return name
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
}

// Chart related function for reducing the data
export function chartDataReducer({ data, nameKey, dataKey }) {
    const limit = 3 // show first 3 items in chart
    // eslint-disable-next-line
    const result = []
    let limitObjCount = 0
    data &&
        data.map((obj, index) => {
            if (index < limit) {
                result.push(obj) // push first 3 items as it is
                limitObjCount += Number(obj[dataKey])
            } else {
                result[limit] = result[limit] || {
                    [nameKey]: "Others",
                    [dataKey]: 0,
                    fill: "#9EC9EA",
                } // mark all the other items as 'Others'
                result[limit][dataKey] += obj[dataKey] // add the value to 'users' count
            }
        })
    if (result.length > limit) {
        while (result[limit][dataKey] >= limitObjCount && result[limit][dataKey] > 0) {
            result[limit][dataKey] = result[limit][dataKey] / 2
        }
    }
    return result
}

export const capitalizeFirstLetter = (str) =>
    str.length ? str.charAt(0).toUpperCase() + str.slice(1) : str

// 🔥 add at top (global scope)

// ================= GLOBALS =================
const backgroundRefreshRequests = {}
const backgroundRefreshMeta = {} // 🔥 cooldown tracker
const BACKGROUND_COOLDOWN = 15000 // 15 sec (adjust if needed)

// ============================================

export const loadAssetExtensionFile = async (file_name, app_key) => {
    const fileName = file_name
    const fileKey = `${app_key}_${fileName}`
    const cacheKey = `temp_file_${fileKey}`

    if (!fileName) return null

    const url = `${process.env.REACT_APP_API_URL}static/media-files/asset/${app_key}/dynamic-action/extensions/${fileName}?cacheBust=${Math.random()}`

    // =====================================================
    // 🔥 SAFE BACKGROUND REFRESH (single guaranteed)
    // =====================================================
    const triggerBackgroundRefresh = () => {
        const now = Date.now()

        // 🚫 if main fetch running → skip
        if (pendingRequests[fileKey]) return

        // 🚫 if background already running → skip
        if (backgroundRefreshRequests[fileKey]) return

        // 🚫 cooldown guard (VERY IMPORTANT)
        const lastRun = backgroundRefreshMeta[fileKey] || 0
        if (now - lastRun < BACKGROUND_COOLDOWN) return

        backgroundRefreshMeta[fileKey] = now

        backgroundRefreshRequests[fileKey] = axios
            .get(url, { responseType: "text" })
            .then((res) => {
                if (res.data !== sharedFileCache[fileKey]) {
                    try {
                        const classNameMatch = res.data.match(/class\s+([A-Za-z0-9_]+)/)
                        const className = classNameMatch?.[1]

                        sharedFileCache[fileKey] = res.data

                        if (className) {
                            injectGlobalClass(className, res.data)
                        }

                        localStorage.setItem(cacheKey, res.data)
                    } catch (err) {
                        console.error("Background refresh failed:", err)
                    }
                }
            })
            .catch(() => {})
            .finally(() => {
                delete backgroundRefreshRequests[fileKey]
            })
    }

    // =========================
    // ✅ In-memory cache
    // =========================
    if (sharedFileCache[fileKey]) {
        triggerBackgroundRefresh()
        return sharedFileCache[fileKey]
    }

    // =========================
    // ✅ localStorage cache
    // =========================
    const cachedFile = localStorage.getItem(cacheKey)
    if (cachedFile) {
        sharedFileCache[fileKey] = cachedFile
        triggerBackgroundRefresh()
        return cachedFile
    }

    // =========================
    // ✅ pending request guard
    // =========================
    if (pendingRequests[fileKey]) {
        return pendingRequests[fileKey]
    }

    // =========================
    // ✅ network fetch
    // =========================
    try {
        pendingRequests[fileKey] = axios
            .get(url, { responseType: "text" })
            .then((response) => {
                const rawJS = response.data

                try {
                    const classNameMatch = rawJS.match(/class\s+([A-Za-z0-9_]+)/)
                    if (!classNameMatch) {
                        console.warn("No class found in JS file")
                        return []
                    }

                    const className = classNameMatch[1]

                    const getContainerClass = new Function(`${rawJS}; return ${className};`)
                    const ContainerClass = getContainerClass()

                    if (ContainerClass && ContainerClass.getAssetReportSQLQueries) {
                        const queries = ContainerClass.getAssetReportSQLQueries()
                        localStorage.setItem(
                            `Asset_Report_Queries_${getSlugFromURL()}`,
                            JSON.stringify(queries),
                        )
                    }

                    sharedFileCache[fileKey] = rawJS
                    localStorage.setItem(cacheKey, rawJS)

                    return rawJS
                } catch (err) {
                    console.error("Failed to execute JS file safely:", err)
                    return []
                }
            })
            .catch((err) => {
                console.error("Failed to fetch JS file:", err)
            })
            .finally(() => {
                delete pendingRequests[fileKey]
            })

        return await pendingRequests[fileKey]
    } catch (error) {
        console.error("Error fetching file:", error)
        throw error
    }
}

export function formattedTime(a) {
    const d = Number(a)
    var h = Math.floor(d / 3600)
    var m = Math.floor((d % 3600) / 60)
    var s = Math.floor((d % 3600) % 60)

    var hDisplay = h > 0 ? h + (h == 1 ? " Hr " : " Hr ") : ""
    var mDisplay = m > 0 ? m + (m == 1 ? " Min " : " Mins ") : ""
    var sDisplay = s > 0 ? s + (s == 1 ? " Second" : " Seconds") : ""
    return hDisplay + mDisplay
}

export const executeWindowFunction = async (className, functionName, ...args) => {
    try {
        if (typeof window === "undefined") return null

        const extensionClass = window?.[className]
        if (!extensionClass) return null

        const fn = extensionClass[functionName]
        if (typeof fn !== "function") return null

        // 🔥 CRITICAL FIX: bind `this` to the class
        const result = fn.call(extensionClass, ...args)

        return result instanceof Promise ? await result : result
    } catch (error) {
        console.error(`[DynamicExtension] Error executing ${functionName} on ${className}`, error)
        return null
    }
}

/**
 * Loads an asset extension file and injects it as a global class
 * @param {string} fileName - The name of the extension file to load
 * @param {string} className - The name of the class to inject
 * @param {string} appKey - The application key for the asset
 * @returns {Promise<Object|null>} The injected class or null if loading/injection failed
 */
export const loadAndInjectExtension = async (fileName, className, appKey) => {
    try {
        const fileContent = await loadAssetExtensionFile(fileName, appKey)
        if (!fileContent) {
            console.warn(`[DynamicExtension] Failed to load file: ${fileName}`)
            return null
        }
        return injectGlobalClass(className, fileContent)
    } catch (error) {
        console.error(
            `[DynamicExtension] Error loading and injecting ${className} from ${fileName}`,
            error,
        )
        return null
    }
}

/**
 * Injects a JavaScript class into the global window object
 * @param {string} className - The name of the class to inject
 * @param {string} fileContent - The JavaScript code containing the class definition
 * @returns {Object|null} The injected class or null if injection failed
 */
export const injectGlobalClass = (className, fileContent) => {
    try {
        if (typeof window === "undefined") {
            console.warn("[DynamicExtension] Window object not available")
            return null
        }

        if (!className || !fileContent) {
            console.warn("[DynamicExtension] Class name and file content are required")
            return null
        }

        // Execute the JavaScript code and inject the class into global scope
        // eslint-disable-next-line no-eval
        eval(`
            ${fileContent}
            globalThis['${className}'] = ${className};
        `)

        // Check if the class was successfully injected
        if (typeof globalThis[className] !== "undefined") {
            return globalThis[className]
        } else {
            console.warn(`[DynamicExtension] Class ${className} not found after evaluation`)
            return null
        }
    } catch (error) {
        console.error(`[DynamicExtension] Error injecting class ${className}`, error)
        return null
    }
}

function delay(ms) {
    return new Promise((res) => setTimeout(res, ms))
}

export const getVoiceiiTagTranslation = async () => {
    try {
        const url = "api/v2/resource-strings"

        const resp = await FetchHelper.get(url, null, {
            "App-Key": "master",
            "Auth-Token": process.env.REACT_APP_STATIC_AUTH_TOKEN,
        })
        if (resp?.status === 200) {
            localStorage.setItem("voiceiiObjectData", JSON.stringify({ ...resp?.data?.data }))
            return { ...resp?.data?.data }
        }
    } catch (err) {
        console.log("error ------>", err)
    }
}
export function updateVoiceiiLocalisation(htmlString, transformFn) {
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlString, "text/html")

    // Existing title translation
    doc.querySelectorAll("[title]").forEach((el) => {
        const oldTitle = el.getAttribute("title")
        if (oldTitle) {
            el.setAttribute("title", transformFn(oldTitle))
        }
    })

    // New translation keys
    doc.querySelectorAll("[data-voiceii-key]").forEach((el) => {
        const key = el.getAttribute("data-voiceii-key")
        if (key) {
            el.textContent = transformFn(key)
        }
    })

    return doc.body.innerHTML
}

export const voiceiiTranslation = (text, language, voiceiiTags, data) => {
    let translatedText = voiceiiTags?.[text]?.[language] || text

    // Replace placeholders like {{date}}
    if (data) {
        translatedText = translatedText.replace(
            /\{\{(.*?)\}\}/g,
            (_, key) => data?.[key.trim()] ?? `{{${key}}}`,
        )
    }
    return translatedText
}
export const replacePlaceholder = (template, value) => {
    if (template && value) {
        return template?.replace(/{}/, value)
    } else {
        return template
    }
}

export const formatLabelWithVoiceii = (label, voiceiiTags) => {
    if (!label) return label

    const keys = []

    //  {key} → {} and store keys
    const template = label.replace(/{(\w+)}/g, (_, key) => {
        keys.push(key)
        return "{}"
    })

    // Translate plain text
    const translatedTemplate = voiceiiTags(template)

    // Put back {key} (NOT value)
    let index = 0
    const finalLabel = translatedTemplate.replace(/{}/g, () => {
        const key = keys[index]
        index++
        return `{${key}}`
    })
    return finalLabel
}

export const updateLanguage = async (language, setLanguage, setVoiceiiObjectData, voiceiiTags) => {
    try {
        const resp = await FetchHelper.patch("api/v1/user/update-language", { language })

        if (resp?.data?.status) {
            setLanguage(language)
            localStorage.setItem("language", language)
            showSuccess(`${voiceiiTags(resp?.data?.message)}`)
        }
        const tag = await getVoiceiiTagTranslation()
        if (tag && setVoiceiiObjectData) setVoiceiiObjectData(tag)
    } catch (err) {
        console.warn("err", err)
        showError(voiceiiTags(err?.response?.data?.message))
    }
}

/**
 * Get the current slug value from URL query parameters
 * @returns {string|null} slug value or null if not present
 */
export const getSlugFromURL = () => {
    const params = new URLSearchParams(window.location.search)
    return params.get("slug")
}
export const getEntityIdFromURL = () => {
    const params = new URLSearchParams(window.location.search)
    return params.get("entityid")
}

/**
 * Converts an asset slug into a JavaScript class name.
 *
 *
 * How it works:
 * - Accepts a snake_case string (asset slug)
 * - Capitalizes each word
 * - Joins them to form a PascalCase class name
 *
 * Input examples:
 * - "gps_tracker"
 * - "average_fill_level_results"
 *
 * Output examples:
 * - "GpsTracker"
 * - "AverageFillLevelResults"
 *
 * @param {string} asset_type - Asset slug in snake_case (optional)
 * @returns {string} PascalCase class name
 */
export const getAssetClassNameFromSlug = (asset_type) => {
    const name = asset_type ? asset_type : getSlugFromURL() || ""

    const formattedName = name
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join("")

    return formattedName
}

//TODO: Need to handle it when language localization is added (for French and English)
export function formattingValue(value, locale = "en-US") {
    // Case 1: value is a number
    if (typeof value === "number") {
        const isFloat = value % 1 !== 0

        return new Intl.NumberFormat(locale, {
            minimumFractionDigits: isFloat ? 2 : 0,
            maximumFractionDigits: isFloat ? 2 : 0,
        }).format(value)
    }

    // Case 2: value is string with number + unit (e.g. "105.0 cyd")
    if (typeof value === "string") {
        // Extract numeric part and unit
        const match = value.match(/^([\d,.]+)\s*(.*)$/)

        if (!match) return value // fallback if unexpected format

        let [, numericPart, unit] = match

        // Normalize number (remove commas, handle decimals)
        const number = Number(numericPart.replace(/,/g, ""))

        if (isNaN(number)) return value

        const isFloat = number % 1 !== 0

        const formattedNumber = new Intl.NumberFormat(locale, {
            minimumFractionDigits: isFloat ? 2 : 0,
            maximumFractionDigits: isFloat ? 2 : 0,
        }).format(number)

        return unit ? `${formattedNumber} ${unit}` : formattedNumber
    }

    // Fallback
    return value
}

// Data table related common functions

export const isPointer = (type, row) => {
    if (type === "lock" && location.pathname === "/assets-detail") {
        if (row?.values?.action_properties?.length > 0) {
            return "pointer"
        } else {
            return "default"
        }
    } else {
        return "pointer"
    }
}
export const applyFillToSvg = (svgString, color = "#000") => {
    if (!svgString || typeof svgString !== "string") return svgString

    try {
        const parser = new DOMParser()
        const doc = parser.parseFromString(svgString, "image/svg+xml")
        const svg = doc.querySelector("svg")

        if (!svg) return svgString

        svg.querySelectorAll("path, circle, rect, ellipse, polygon, line, polyline").forEach(
            (el) => {
                const fill = el.getAttribute("fill")
                const stroke = el.getAttribute("stroke")

                // FILL: attribute exists AND value is empty
                if (fill === "") {
                    el.setAttribute("fill", color)
                }

                // STROKE: attribute exists AND value is empty
                if (stroke === "") {
                    el.setAttribute("stroke", color)
                }
            },
        )

        return svg.outerHTML
    } catch (e) {
        console.error("SVG fill/stroke apply failed", e)
        return svgString
    }
}

export function getActionIconValue(parentObject, actionProperty) {
    // Step 1: Extract the key from the actionProperty object
    const { key, action_icons } = actionProperty
    // Step 2: Find the value of the key in the parent object (first level of the object)
    const parentValue = parentObject[key]

    // Step 3: Iterate through the action_icons object
    for (const [iconKey, iconValue] of Object.entries(action_icons || {})) {
        // Step 4: Compare the key with the parentValue
        if (iconKey === parentValue) {
            // Return the matching value
            return iconValue
        }
    }

    if (parentValue === undefined) {
        const firstIconValue = Object.values(action_icons)[0]

        return firstIconValue
    }

    // Return null if no match is found
    return null
}

export const convertUTCToLocalForDatatable = (utcDt, utcDtFormat) => {
    if (!utcDt) {
        return {
            difference: 0,
            time: null,
        }
    }

    const zone_name = moment.tz.guess()

    // 🔧 Normalize microseconds → milliseconds
    let normalized = utcDt
    if (typeof utcDt === "string" && utcDt.includes(".")) {
        normalized = utcDt.replace(/(\.\d{3})\d+/, "$1")
    }

    const toDt = moment.utc(normalized)
    const current_time = toDt.tz(zone_name)

    const diffInMinutes = moment().diff(current_time, "minutes")

    return {
        difference: diffInMinutes,
        time: current_time.toDate(), // ✅ RAW DATE OBJECT
    }
}

export let hiddenColumns = []

export const sanitizeSuffixUnit = (val) => {
    const sanitized = String(val).toLowerCase()

    if (
        sanitized === "" ||
        sanitized.toLowerCase() === "null" ||
        sanitized.toLowerCase() === "undefined" ||
        sanitized.toLowerCase() === "nan"
    ) {
        return ""
    }

    return sanitized
}

export const sanitizeValue = (val, fallback = "N/A") => {
    // null / undefined
    if (val == null) return fallback

    // convert to string & trim
    const sanitized = String(val).trim()

    // empty or invalid strings
    if (
        sanitized === "" ||
        sanitized.toLowerCase() === "null" ||
        sanitized.toLowerCase() === "undefined" ||
        sanitized.toLowerCase() === "nan"
    ) {
        return fallback
    }

    return sanitized
}

export const computeColumns = ({ data, meta, action, formatDate }) => {
    hiddenColumns = [] // reset hidden columns
    let columns = []

    if (!data || !data.length) return columns

    const keys = Object.keys(data[0] || [])
    let conversion = { difference: 0, time: "" }
    let styledData = ""

    columns = keys.map((value) => {
        const metaData = meta?.[value]
        // ✅ handle visibility (new meta)
        const shouldHide = metaData?.visibility?.show === false
        if (shouldHide) {
            hiddenColumns.push(value)
        }

        return {
            id: value,
            accessor: value,

            Header: metaData?.formatting?.header || value.replace(/_/g, " "),

            // ✅ REQUIRED for react-table filtering
            filter: metaData?.filters?.filter,
            canFilter: !!metaData?.filters?.filterType,
            disableFilters: metaData?.filters?.disableFilter,

            // ✅ DO NOT override this later
            // Use explicit filterType, or infer from type field for date/datetime
            filters: metaData?.filters?.filterType
                ? { filterType: metaData.filters.filterType }
                : { filterType: "text" },

            Cell: ({
                value,
                cell: {
                    row: { original },
                },
            }) => {
                switch (metaData?.formatting?.style_type || metaData?.type) {
                    case "styled_on_battery_low":
                        return (
                            <div style={{ color: value <= 30 ? "red" : "green" }}>
                                {sanitizeValue(value) +
                                    sanitizeSuffixUnit(metaData?.formatting?.suffix)}
                            </div>
                        )

                    case "styled_lock_status":
                        return (
                            <div
                                style={{
                                    color:
                                        value === "Locked"
                                            ? "red"
                                            : value === "unLocked"
                                              ? "green"
                                              : "",
                                }}
                            >
                                {sanitizeValue(value)}
                            </div>
                        )

                    case "styled_on_update":
                        conversion = convertUTCToLocalForDatatable(original.updated_at)

                        if (conversion.difference < 5) styledData = "green"
                        else if (conversion.difference < 10) styledData = "orange"
                        else styledData = "red"

                        return (
                            <div style={{ color: styledData }}>
                                {sanitizeValue(value) +
                                    sanitizeSuffixUnit(metaData?.formatting?.suffix)}
                            </div>
                        )

                    case "button":
                        return (
                            <div
                                style={{ cursor: "pointer", color: "blue" }}
                                onClick={() => action({ row: original, meta: metaData })}
                            >
                                {sanitizeValue(value)}
                            </div>
                        )

                    case "format_string":
                        return (
                            <div className="text-capitalize">
                                {sanitizeValue(value?.split("_")?.join(" "))}
                            </div>
                        )

                    case "email":
                        return <div>{Array.isArray(value) ? value.join(", ") : value}</div>

                    case "date":
                        return value ? <div>{value ? value : "N/A"}</div> : "-"
                    case "datetime":
                        conversion = convertUTCToLocalForDatatable(value)
                        return value ? (
                            <div>{conversion.time ? formatDate(conversion.time) : "N/A"}</div>
                        ) : (
                            "-"
                        )

                    default:
                        if (typeof value === "object" && value !== null) {
                            return (
                                <div style={{ textTransform: "capitalize" }}>
                                    {sanitizeValue(value?.table_name)}
                                </div>
                            )
                        }

                        return (
                            <div style={{ textTransform: "capitalize" }}>
                                {typeof value === "boolean"
                                    ? value
                                        ? "Yes"
                                        : "No"
                                    : sanitizeValue(value) +
                                      sanitizeSuffixUnit(metaData?.formatting?.suffix)}
                            </div>
                        )
                }
            },

            renderFilter: ({
                filterType,
                data,
                onChange,
                value,
                handleRangeChange,
                rangefilterValue,
            }) => {
                let jsx = null

                switch (filterType) {
                    case "select": {
                        const options = new Set()
                        data.preFilteredRows.forEach((row) => {
                            options.add(row.values[data.id])
                        })

                        jsx = (
                            <Form.Select onChange={onChange} value={value}>
                                <option value="">Select</option>
                                {[...options].map((opt, i) => (
                                    <option key={i} value={opt}>
                                        {opt}
                                    </option>
                                ))}
                            </Form.Select>
                        )
                        break
                    }

                    case "range-slider":
                        jsx = (
                            <Slider
                                value={rangefilterValue}
                                onChange={handleRangeChange}
                                valueLabelDisplay="auto"
                            />
                        )
                        break

                    default:
                        jsx = (
                            <Form.Control
                                type="text"
                                value={value}
                                onChange={onChange}
                                placeholder={data.Header}
                            />
                        )
                }

                return data.canFilter && jsx
            },

            width: 50,
            ...metaData,
        }
    })
    return columns
}

const renderOption = (option) => {
    if (option) {
        return "Yes"
    } else {
        return "No"
    }
}

export const renderTableCellContent = ({
    cell,
    isPercentage,
    renderProgressBar,
    getSlugFromURL,
}) => {
    const value = cell.value
    const rowData = cell?.row?.original
    const hyperlink = cell?.column?.hyperlink

    // Percentage case
    if (isPercentage && typeof value === "number") {
        return renderProgressBar(value)
    }

    // Hyperlink case
    if (hyperlink) {
        const finalUrl = generateHyperlink(hyperlink, rowData)

        // SAME ORIGIN (internal navigation)
        if (hyperlink.same_origin) {
            return (
                <MuiLink
                    component={RouterLink}
                    to={finalUrl}
                    state={{ data: rowData }}
                    underline="hover"
                    color="primary"
                    sx={{
                        cursor: "pointer",
                        "&:hover": {
                            textDecoration: "underline",
                            color: "primary.main",
                        },
                    }}
                >
                    {cell.render("Cell")}
                </MuiLink>
            )
        }

        // EXTERNAL LINK
        return (
            <MuiLink
                href={finalUrl}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                color="primary"
                sx={{ cursor: "pointer" }}
            >
                {cell.render("Cell")}
            </MuiLink>
        )
    }

    // Default case
    return cell.render("Cell")
}

export const generateHyperlink = (hyperlink, rowData) => {
    if (!hyperlink?.endpoint) return ""

    let finalEndpoint = hyperlink.endpoint

    // If dynamic endpoint → replace {keys}
    if (hyperlink.dynamic_endpoint) {
        const matches = finalEndpoint.match(/{(.*?)}/g) // ['{caregiver}', '{abcdkl_2}']

        if (matches) {
            matches.forEach((match) => {
                const key = match.replace(/[{}]/g, "") // caregiver
                const value = rowData?.[key] ?? ""

                finalEndpoint = finalEndpoint.replace(match, value)
            })
        }
    }

    return finalEndpoint
}

// Our table component

// Progress bar component for percentage data
export const renderProgressBar = (percentage, showText = true) => {
    const validPercentage = Math.max(0, Math.min(100, percentage || 0))

    return (
        <div style={{ display: "inline-flex", alignItems: "center", width: "100%", gap: "8px" }}>
            <div
                className="table-progress-bar"
                style={{
                    flex: 1,
                    height: "8px",
                    backgroundColor: "#f0f0f0",
                    borderRadius: "4px",
                    overflow: "hidden",
                }}
            >
                <div
                    className="table-progress-fill"
                    style={{
                        width: `${validPercentage}%`,
                        height: "100%",
                        backgroundColor: "var(--dt-primary, #6c5ffc)",
                        borderRadius: "4px",
                    }}
                />
            </div>
            {showText && (
                <span
                    className="table-progress-text"
                    style={{ fontSize: "13px", fontWeight: "600", minWidth: "35px" }}
                >
                    {validPercentage}%
                </span>
            )}
        </div>
    )
}

export const renderFilter = ({
    showSlider,
    filterType,
    data,
    onChange,
    value,
    handleRangeChange,
    rangefilterValue,
    voiceiiTags,
}) => {
    let jsx = null
    switch (filterType) {
        case "select":
            const options = new Set()
            data.preFilteredRows.forEach((row) => {
                !checkFalsey(row.values[data.id]) && options.add(row.values[data.id])
            })
            const optionData = [...options.values()]
                .filter((v) => v != null) // remove null/undefined
                .map(String) // convert everything to string
                .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
                .map((str) =>
                    str
                        .split(" ")
                        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join(" "),
                )

            jsx = (
                <Form.Select
                    type="text"
                    placeholder={voiceiiTags("All...")}
                    onChange={onChange}
                    value={value ?? ""} // ✅ ensures controlled input resets
                    className="table-filter-select"
                >
                    <option value="">{voiceiiTags("All...")}</option>
                    {optionData.map((option, i) => (
                        <option key={i} value={option} className="text-capitalize">
                            {data.id === "is_active"
                                ? voiceiiTags(renderOption(option))
                                : voiceiiTags(option)}
                        </option>
                    ))}
                </Form.Select>
            )
            break
        case "range-slider":
            jsx = (
                <div style={{ padding: "0 10px" }}>
                    {showSlider ? (
                        <Slider
                            value={rangefilterValue}
                            onChange={handleRangeChange}
                            valueLabelDisplay="auto"
                            size="small"
                            sx={{
                                color: "var(--dt-primary)",
                                height: 4,
                                "& .MuiSlider-thumb": {
                                    width: 12,
                                    height: 12,
                                },
                            }}
                        />
                    ) : (
                        <div
                            className="range-wrapper"
                            style={{ border: "none", background: "transparent", padding: 0 }}
                        >
                            <span className="range-value" style={{ fontSize: "12px" }}>
                                {rangefilterValue[0]}
                            </span>
                            <div
                                className="line"
                                style={{
                                    width: "20px",
                                    height: "2px",
                                    background: "var(--dt-primary)",
                                }}
                            ></div>
                            <span className="range-value" style={{ fontSize: "12px" }}>
                                {rangefilterValue[1]}
                            </span>
                        </div>
                    )}
                </div>
            )
            break
        case "date": {
            const selectedDate = value || null

            jsx = (
                <div className="d-flex justify-content-start align-items-center">
                    <Flatpickr
                        value={selectedDate || ""}
                        options={{
                            dateFormat: "Y-m-d",
                            allowInput: true,
                        }}
                        onChange={(dates) => {
                            if (!dates || dates.length === 0) {
                                onChange({ target: { value: null } })
                                return
                            }

                            const dateStr = moment(dates[0]).format("YYYY-MM-DD")
                            onChange({ target: { value: dateStr } })
                        }}
                        render={({ value, ...props }, ref) => (
                            <input
                                {...props}
                                ref={ref}
                                className="table-filter-input form-control"
                                placeholder={`${voiceiiTags("Select date...")}`}
                            />
                        )}
                    />

                    {selectedDate && (
                        <IconButton
                            size="small"
                            onClick={() => onChange({ target: { value: null } })}
                            title={`${voiceiiTags("Clear date filter")}`}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}
                </div>
            )
            break
        }

        default:
            jsx = (
                <>
                    <Form.Control
                        type="text"
                        value={value ?? ""}
                        className="table-filter-input"
                        placeholder={`${voiceiiTags("Filter")} ${voiceiiTags(data.Header)}...`}
                        onChange={onChange}
                    />
                </>
            )
    }

    return data.canFilter && jsx
}

export const updateLocalStoragePageSize = (value, appName, pathName) => {
    const existingData = JSON.parse(localStorage.getItem(appName)) || {}
    existingData[pathName] = value
    localStorage.setItem(appName, JSON.stringify(existingData))
}

const page_name_ar = [
    "iot_history",
    "station_history",
    "assets_history",
    "alert_logs",
    "admin_logs",
]
export const showModalWithProperScrollBar = (pageName) => {
    return page_name_ar.includes(pageName)
}

export const isAssetDetailScreen = location.pathname === "/assets-detail"

export const renderLockBtn = (row, iconsColor) => {
    return location.pathname == "/assets-detail" ? (
        row?.values?.action_properties?.length > 0 ? (
            <LockIcon
                style={{
                    color: iconsColor,
                }}
            />
        ) : (
            <div style={{ marginLeft: "24px" }}></div>
        )
    ) : (
        <LockIcon
            style={{
                color: iconsColor,
            }}
        />
    )
}

// helper (put this near top of file)
export const sanitize = (type, value) => {
    if (type === "number") {
        // Keep digits, one optional minus at start, and one decimal point
        let cleaned = value.replace(/[^0-9.-]/g, "")

        // Ensure minus sign is only at the start
        if (cleaned.includes("-")) {
            cleaned = `-${cleaned.replace(/-/g, "")}`
        }

        // Ensure only one decimal point
        const parts = cleaned.split(".")
        if (parts.length > 2) {
            cleaned = `${parts[0]}.${parts.slice(1).join("")}`
        }

        return cleaned
    }

    // For text: allow alphanumeric and spaces
    return value.replace(/[^a-zA-Z0-9 ]+/g, "")
}

// Update values to containers after user updates the data --> Update logic
export function updateArray(newArray, oldArray) {
    const updatedOldArray = oldArray ? [...oldArray] : [] // Spread oldArray if it's not null, otherwise return an empty array

    newArray?.forEach((newItem) => {
        const index = updatedOldArray.findIndex((oldItem) => oldItem.uuid === newItem.uuid)
        if (index !== -1) {
            updatedOldArray[index] = newItem // Optionally update the item
        } else {
            updatedOldArray.push(newItem) // Append new item to updatedOldArray
        }
    })

    return updatedOldArray
}

export const ModalContentMemonized = (row, columnMeta) => {
    switch (columnMeta.action) {
        default:
            return `Content Not Defined for: ${columnMeta.action}`
    }
}

// Reporting page related functions mentioned below --->

// Recursive function to format data for rc-cascader
export const formatOptions = (items, voiceiiTags) =>
    items?.map((item) => ({
        value: item.key,
        label: voiceiiTags ? voiceiiTags(item.label) : item.label,
        children:
            item.value && item.value.length > 0 ? formatOptions(item.value, voiceiiTags) : null,
    }))

export function mergeWithWrapper(input, separator = "", wrapperFn = (v) => String(v)) {
    const arr = Array.isArray(input) ? input : [input]

    return arr.map((item) => wrapperFn(item)).join(separator)
}

// Builds a filtered hierarchical structure from the full data based on selected zone values.
export const getSelectedHierarchy = (dataArray, selectedZoneValues) => {
    const result = []

    for (const filterName in selectedZoneValues) {
        if (selectedZoneValues.hasOwnProperty(filterName)) {
            const filterSelections = selectedZoneValues[filterName]

            const matchingFilter = dataArray.find((item) => item.label === filterName)

            if (matchingFilter) {
                const formattedFilter = {
                    ...matchingFilter,
                    value: [],
                    modified: true,
                }

                const buildHierarchy = (currentLevel, selections) => {
                    if (!selections.length) return []

                    const currentKey = selections[0]
                    const nextSelections = selections.slice(1)

                    return currentLevel
                        .filter((item) => item.key.trim() === currentKey.trim())
                        .map((item) => ({
                            ...item,
                            value: buildHierarchy(item.value || [], nextSelections),
                        }))
                }

                formattedFilter.value = buildHierarchy(matchingFilter.value, filterSelections)
                result.push(formattedFilter)
            }
        }
    }

    return result
}

/**
 * Get a valid local datetime moment instance for UTC conversion.
 *
 * @param {string|Date} input - Local datetime value from form input.
 * @returns {moment.Moment|null} Parsed local datetime moment.
 */
const getParsedLocalDateTimeForUTC = (input) => {
    if (!input) return null

    if (input instanceof Date) {
        const parsedDate = moment(input)
        return parsedDate.isValid() ? parsedDate : null
    }

    if (typeof input === "string") {
        const normalizedValue = normalizeUTCDateTimeValue(input)
        if (!normalizedValue) return null

        let parsedDate = moment(normalizedValue, LOCAL_DATE_TIME_UTC_FORMATS, true)

        if (!parsedDate.isValid()) {
            parsedDate = moment(normalizedValue, moment.ISO_8601, true)
        }

        if (!parsedDate.isValid()) {
            parsedDate = moment(normalizedValue)
        }

        return parsedDate.isValid() ? parsedDate : null
    }

    const parsedDate = moment(input)
    return parsedDate.isValid() ? parsedDate : null
}

/**
 * Format a JavaScript Date object into a local 24-hour datetime string.
 *
 * @param {Date} input - Local Date object from the form control.
 * @returns {string} Local datetime string for form state.
 */
export const formatDateObjectToLocalDateTimeString = (input) => {
    if (!(input instanceof Date) || isNaN(input.getTime())) return ""

    const year = input.getFullYear()
    const month = String(input.getMonth() + 1).padStart(2, "0")
    const day = String(input.getDate()).padStart(2, "0")
    const hours = String(input.getHours()).padStart(2, "0")
    const minutes = String(input.getMinutes()).padStart(2, "0")

    return `${year}-${month}-${day} ${hours}:${minutes}`
}

/**
 * Convert a local datetime form value into a Date object for Flatpickr.
 *
 * @param {string|Date} localDateTimeValue - Local datetime value from form state.
 * @returns {Date|null} Date object for datetime picker rendering.
 */
export const convertLocalDateTimeValueToDateObject = (localDateTimeValue) => {
    if (!localDateTimeValue) return null

    if (localDateTimeValue instanceof Date) {
        return isNaN(localDateTimeValue.getTime()) ? null : localDateTimeValue
    }

    const parsedLocalDateTime = getParsedLocalDateTimeForUTC(localDateTimeValue)
    if (!parsedLocalDateTime) return null

    return parsedLocalDateTime.toDate()
}

/**
 * Convert local date and time strings into synchronized UTC date/time payload values.
 *
 * Use this when separate date and time fields together represent a single local
 * moment that the backend stores in UTC.
 *
 * @param {string} localDateString - Local date selected in the form.
 * @param {string} localTimeString - Local time selected in the form.
 * @returns {{ date: string, time: string, datetime: string }|null} UTC split values.
 */
export const convertLocalDateAndTimeToUTCSplitValues = (localDateString, localTimeString) => {
    if (!localDateString || !localTimeString) return null

    const combinedLocalDateTime = `${localDateString} ${localTimeString}`
    const parsedLocalDateTime = moment(
        combinedLocalDateTime,
        ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"],
        true,
    )

    if (!parsedLocalDateTime.isValid()) return null

    const utcDateTime = parsedLocalDateTime.clone().utc()

    return {
        date: utcDateTime.format("YYYY-MM-DD"),
        time: utcDateTime.format("HH:mm:ss"),
        datetime: `${utcDateTime.format("YYYY-MM-DD HH:mm:ss.SSS")}000`,
    }
}

/**
 * Format a local datetime value into the backend-required UTC datetime string
 * with fixed microseconds.
 *
 * Format:
 * YYYY-MM-DD HH:mm:ss.000000
 *
 * Example output:
 * 1995-11-11 00:00:00.000000
 *
 * - Parses string inputs with strict 24-hour formats before UTC conversion.
 * - Appends fixed microseconds (.000000) as required by backend.
 *
 * @param {string|Date} input - Local datetime value selected in the form.
 * @returns {string} Formatted UTC datetime string.
 */
export const formatLocalDateTimeWithMicroseconds = (input) => {
    const parsedDate = getParsedLocalDateTimeForUTC(input)
    if (!parsedDate) return ""

    return `${parsedDate.clone().utc().format("YYYY-MM-DD HH:mm:ss.SSS")}000`
}

export const convertDateToUTCString = (localDateString) => {
    return convertLocalDateStringToUTCDateString(localDateString)
}

const normalizeUTCDateTimeValue = (utcValue) => {
    if (utcValue === null || utcValue === undefined || utcValue === "") return null

    if (typeof utcValue === "string") {
        const trimmedValue = utcValue.trim()
        if (!trimmedValue) return null

        if (trimmedValue.includes(".")) {
            return trimmedValue.replace(/(\.\d{3})\d+/, "$1")
        }

        return trimmedValue
    }

    return utcValue
}

/**
 * Convert UTC datetime input to local Date object for date-time picker fields.
 *
 * @param {string|Date} utcDateTimeValue - UTC datetime value from backend.
 * @returns {Date|null} Local Date object for UI fields.
 */
export const convertUTCDateTimeToLocalDateObject = (utcDateTimeValue) => {
    if (!utcDateTimeValue) return null

    if (utcDateTimeValue instanceof Date) {
        return isNaN(utcDateTimeValue.getTime()) ? null : utcDateTimeValue
    }

    const normalizedValue = normalizeUTCDateTimeValue(utcDateTimeValue)
    const parsedUtcDateTime = moment.utc(normalizedValue)

    if (!parsedUtcDateTime.isValid()) return null

    return parsedUtcDateTime.local().toDate()
}

/**
 * Convert UTC datetime input to a local 24-hour datetime string for form state.
 *
 * @param {string|Date} utcDateTimeValue - UTC datetime value from backend.
 * @returns {string} Local datetime string for datetime form fields.
 */
export const convertUTCDateTimeToLocalDateTimeString = (utcDateTimeValue) => {
    if (!utcDateTimeValue) return ""

    if (utcDateTimeValue instanceof Date) {
        return formatDateObjectToLocalDateTimeString(utcDateTimeValue)
    }

    const normalizedValue = normalizeUTCDateTimeValue(utcDateTimeValue)
    const parsedUtcDateTime = moment.utc(normalizedValue)

    if (!parsedUtcDateTime.isValid()) return ""

    return parsedUtcDateTime.local().format(LOCAL_DATE_TIME_INPUT_FORMAT)
}

/**
 * Convert UTC date input to localized date string (YYYY-MM-DD).
 *
 * @param {string} utcDateValue - UTC date value from backend.
 * @returns {string} Local date string for form date fields.
 */
export const convertUTCDateToLocalDateString = (utcDateValue) => {
    if (!utcDateValue) return ""

    const normalizedValue = normalizeUTCDateTimeValue(utcDateValue)
    let parsedUtcDate = moment.utc(normalizedValue, "YYYY-MM-DD", true)

    if (!parsedUtcDate.isValid()) {
        parsedUtcDate = moment.utc(normalizedValue)
    }

    if (!parsedUtcDate.isValid()) return ""

    return parsedUtcDate.local().format("YYYY-MM-DD")
}

/**
 * Convert UTC time input to localized time string (HH:mm).
 *
 * @param {string} utcTimeValue - UTC time value from backend.
 * @returns {string} Local time string for form time fields.
 */
export const convertUTCTimeToLocalTimeString = (utcTimeValue) => {
    if (!utcTimeValue) return ""

    const normalizedValue = normalizeUTCDateTimeValue(utcTimeValue)

    let parsedUtcTime = moment.utc(
        normalizedValue,
        ["HH:mm:ss.SSSSSS", "HH:mm:ss.SSS", "HH:mm:ss", "HH:mm"],
        true,
    )

    if (!parsedUtcTime.isValid()) {
        parsedUtcTime = moment.utc(normalizedValue)
    }

    if (!parsedUtcTime.isValid()) return ""

    return parsedUtcTime.local().format("HH:mm")
}

/**
 * Convert a local date string to the backend date format.
 *
 * Date-only fields represent a calendar date rather than a timezone-aware instant,
 * so the selected date must be preserved exactly as entered. When a date field is
 * paired with a time field and both represent one UTC-backed instant, use
 * `convertLocalDateAndTimeToUTCSplitValues` instead.
 *
 * @param {string} localDateString - Local date selected in form.
 * @returns {string|null} Backend date string for payload.
 */
export const convertLocalDateStringToUTCDateString = (localDateString) => {
    if (!localDateString) return null

    const parsedLocalDate = moment(localDateString, "YYYY-MM-DD", true)
    if (!parsedLocalDate.isValid()) return null

    return parsedLocalDate.format("YYYY-MM-DD")
}

/**
 * Convert local time string (HH:mm or HH:mm:ss) to UTC time string (HH:mm:ss).
 *
 * @param {string} localTimeString - Local time selected in form.
 * @returns {string|null} UTC time string for backend payload.
 */
export const convertLocalTimeStringToUTCTimeString = (localTimeString) => {
    if (!localTimeString) return null

    const parsedLocalTime = moment(localTimeString, ["HH:mm:ss", "HH:mm"], true)
    if (!parsedLocalTime.isValid()) return null

    return parsedLocalTime.utc().format("HH:mm:ss")
}

export const buildSchedulePayload = (repeatType) => {
    return {
        is_recurring: repeatType !== "does_not_repeat",
    }
}

export const weekDays = [
    { value: 7, label: "Sun" },
    { value: 1, label: "Mon" },
    { value: 2, label: "Tue" },
    { value: 3, label: "Wed" },
    { value: 4, label: "Thu" },
    { value: 5, label: "Fri" },
    { value: 6, label: "Sat" },
]

export const extractDateOnlyFromDateTimeString = (dateString) => {
    if (!dateString) return ""
    try {
        // If ISO format (contains T)
        if (dateString.includes("T")) {
            return dateString.split("T")[0]
        }

        // If space-separated format
        if (dateString.includes(" ")) {
            return dateString.split(" ")[0]
        }

        // Already just date
        return dateString
    } catch (error) {
        console.error("Invalid date format:", dateString)
        return ""
    }
}

export const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)

        reader.onload = () => resolve(reader.result)
        reader.onerror = (error) => reject(error)
    })
}

export function slugToPascalCase(slug) {
    if (!slug) return ""

    return slug
        .split("_")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join("")
}

export const toTitleCase = (text = "") => {
    return text
        .toLowerCase()
        .replace(/[_-]+/g, " ") // convert _ and - to space
        .replace(/\s+/g, " ") // remove extra spaces
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase())
}

export const isRouteActiveLink = (link) => {
    if (!link) return false
    // External links should never be considered "active" inside the SPA router.
    if (/^https?:\/\//.test(link)) return false

    // Compare the raw route including query string (e.g. `?slug=...`).
    const browserLink = `${location.pathname}${location.search}`

    // Per requirement: compare based on everything after the first `/`.
    const stripLeadingSlash = (s) => (s || "").replace(/^\/+/, "")
    const a = stripLeadingSlash(link)
    const b = stripLeadingSlash(browserLink)

    // If the menu link includes query params, we require an exact match
    // so that `/assets-detail?slug=care_plan_categories` highlights correctly.
    if (a.includes("?")) return a === b

    // For non-query routes (parents), allow prefix matching so `/admin/...`
    // keeps `/admin` highlighted.
    return b === a || b.startsWith(`${a}/`)
}

export const getMetricConfig = (key, iconsColor) => {
    const configs = {
        // Compound keys — checked first (longer = more specific)
        fire_alarm: {
            icon: LocalFireDepartment,
            bgColor: "rgba(232, 38, 70, 0.1)",
            iconColor: "#e82646",
        },
        weight_collected: { icon: Scale, bgColor: "rgba(108, 95, 252, 0.1)", iconColor: "#3489C2" },
        avg_fill: { icon: Percent, bgColor: "rgba(9, 173, 149, 0.1)", iconColor: "#09ad95" },

        // Single-word keys
        total: { icon: Inventory, bgColor: "rgba(108, 95, 252, 0.1)", iconColor: "#3489C2" },
        active: { icon: CheckCircle, bgColor: "rgba(9, 173, 149, 0.1)", iconColor: "#09ad95" },
        offline: { icon: WifiOff, bgColor: "rgba(247, 183, 49, 0.1)", iconColor: "#f7b731" },
        disabled: { icon: Block, bgColor: "rgba(232, 38, 70, 0.1)", iconColor: "#e82646" },
        warning: { icon: Warning, bgColor: "rgba(247, 183, 49, 0.1)", iconColor: "#f7b731" },
        normal: {
            icon: CheckCircleOutline,
            bgColor: "rgba(9, 173, 149, 0.1)",
            iconColor: "#09ad95",
        },
        empty: { icon: Inbox, bgColor: "rgba(113, 112, 128, 0.1)", iconColor: "#717080" },
        pickup: { icon: TouchApp, bgColor: "#f0f5f9", iconColor: "#1170e4" },
        tamper: { icon: Gavel, bgColor: "rgba(247, 183, 49, 0.1)", iconColor: "#f7b731" },
        stations: { icon: Business, bgColor: "rgba(17, 112, 228, 0.1)", iconColor: "#1170e4" },

        // Care / visit semantic keys
        completed: { icon: CheckCircle, bgColor: "rgba(9, 173, 149, 0.1)", iconColor: "#09ad95" },
        missed: { icon: EventBusy, bgColor: "rgba(232, 38, 70, 0.1)", iconColor: "#e82646" },
        exception: { icon: Warning, bgColor: "rgba(247, 183, 49, 0.1)", iconColor: "#f7b731" },
        today: { icon: CalendarToday, bgColor: "rgba(17, 112, 228, 0.1)", iconColor: "#1170e4" },
        avg: { icon: Percent, bgColor: "rgba(9, 173, 149, 0.1)", iconColor: "#09ad95" },
    }

    const normalized = (key || "").toLowerCase()

    // 1. Exact match
    if (configs[normalized]) return configs[normalized]

    // 2. Substring match — longer config keys are checked first for specificity
    const sortedKeys = Object.keys(configs).sort((a, b) => b.length - a.length)
    const partialKey = sortedKeys.find((configKey) => normalized.includes(configKey))
    if (partialKey) return configs[partialKey]

    return {
        icon: Inventory,
        bgColor: "#f0f5f9",
        iconColor: iconsColor || "#3489C2",
    }
}

export const getDatePlaceholder = (format = "Y-m-d") => {
    // Supported date formats (Flatpickr):
    // Y-m-d => YYYY-MM-DD (default)
    // d/m/Y => DD/MM/YYYY
    // m-d-Y => MM-DD-YYYY
    // d-m-Y => DD-MM-YYYY
    // m/d/Y => MM/DD/YYYY

    const placeholders = {
        "Y-m-d": "YYYY-MM-DD",
        "d/m/Y": "DD/MM/YYYY",
        "m-d-Y": "MM-DD-YYYY",
        "d-m-Y": "DD-MM-YYYY",
        "m/d/Y": "MM/DD/YYYY",
    }

    return placeholders[format] || "YYYY-MM-DD"
}
